import { Avatar, AvatarGroup, Box, Chip, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { ICertificateEmployeeDTO } from '@sharedInterfaces/ICertificate';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { EmployeePicture } from '@src/App/Components/General/EmployeePicture/EmployeePicture';
import type { AppState } from '@store/store';
import { Award, Calendar, CheckCircle, Users } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

interface CertificatesListProps {
    certificates: ICertificateEmployeeDTO[];
    viewMode: 'list' | 'cards';
}

function CertificatesList({ certificates, viewMode }: CertificatesListProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).certificates;
    const allCertificates = useSelector((state: AppState) => state.certificates || []);

    const getCertificateLogo = (title: string) => {
        const lowerTitle = title.toLowerCase();
        if (lowerTitle.includes('aws')) return '☁️';
        if (lowerTitle.includes('microsoft') || lowerTitle.includes('azure')) return '🔷';
        if (lowerTitle.includes('google')) return '🔍';
        if (lowerTitle.includes('scrum')) return '🏃♂️';
        if (lowerTitle.includes('docker')) return '🐳';
        if (lowerTitle.includes('kubernetes')) return '⚙️';
        return '🏆';
    };

    const getStatusColor = (certificate: ICertificateEmployeeDTO) => {
        if (!certificate.endDate) return theme.palette.success.main;

        const now = new Date();
        const twoMonthsFromNow = new Date();
        twoMonthsFromNow.setMonth(twoMonthsFromNow.getMonth() + 2);

        if (certificate.endDate <= now) return theme.palette.error.main;
        if (certificate.endDate <= twoMonthsFromNow) return theme.palette.warning.main;
        return theme.palette.success.main;
    };

    const getStatusText = (certificate: ICertificateEmployeeDTO) => {
        if (!certificate.endDate) return 'Active';

        const now = new Date();
        const twoMonthsFromNow = new Date();
        twoMonthsFromNow.setMonth(twoMonthsFromNow.getMonth() + 2);

        if (certificate.endDate <= now) return 'Expired';
        if (certificate.endDate <= twoMonthsFromNow) return 'Expiring Soon';
        return 'Active';
    };

    if (certificates.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Award size={48} color={theme.palette.text.disabled} />
                <Typography variant="body2" sx={{ mt: 2, opacity: 0.6 }}>
                    {langStrings.noResults}
                </Typography>
            </Box>
        );
    }

    if (viewMode === 'cards') {
        return (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                {certificates.map((certificate) => {
                    const certificateData = allCertificates.find((cert) => cert.id === certificate.id);
                    const employeesWithCert = certificateData?.employees || [];

                    return (
                        <Paper
                            key={certificate.id}
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                background: alpha(theme.palette.background.paper, 0.8),
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                cursor: 'pointer',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                    borderColor: alpha(theme.palette.primary.main, 0.3),
                                },
                                transition: 'all 0.2s ease',
                            }}
                            onClick={() => navigate(`/${ELinks.CERTIFICATES}/${certificate.id}`)}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                                <Box
                                    component="img"
                                    src="/certificates/certificate-placeholder.svg"
                                    alt="Certificate"
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: 1,
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                                    }}
                                />

                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 400,
                                            mb: 1,
                                            fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.9rem' },
                                        }}
                                    >
                                        {certificate.title}
                                    </Typography>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Chip
                                            icon={<CheckCircle size={12} />}
                                            label={getStatusText(certificate)}
                                            size="small"
                                            sx={{
                                                bgcolor: alpha(getStatusColor(certificate), 0.1),
                                                color: getStatusColor(certificate),
                                                fontWeight: 500,
                                                fontSize: '0.7rem',
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>

                            {/* Certificate Details */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {certificate.startDate && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Calendar size={14} color={theme.palette.text.secondary} />
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                            {langStrings.obtained}: {certificate.startDate.toLocaleDateString('de-DE')}
                                        </Typography>
                                    </Box>
                                )}

                                {certificate.endDate && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Calendar size={14} color={theme.palette.text.secondary} />
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                            {langStrings.expirationDate}:{' '}
                                            {certificate.endDate.toLocaleDateString('de-DE')}
                                        </Typography>
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircle size={14} color={getStatusColor(certificate)} />
                                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                        {langStrings.status}: {getStatusText(certificate)}
                                    </Typography>
                                </Box>

                                {/* Employees with this certificate */}
                                <Box
                                    sx={{
                                        mt: 1,
                                        pt: 1.5,
                                        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Users size={14} color={theme.palette.text.secondary} />
                                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                                            {langStrings.employeesWithCertificate}:
                                        </Typography>
                                    </Box>

                                    <AvatarGroup
                                        max={4}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            '& .MuiAvatar-root': {
                                                width: 28,
                                                height: 28,
                                                fontSize: '0.7rem',
                                                border: `2px solid ${theme.palette.background.paper}`,
                                            },
                                        }}
                                    >
                                        {employeesWithCert.slice(0, 4).map((emp) => {
                                            const initials = emp.title
                                                .split(' ')
                                                .map((n) => n[0])
                                                .join('')
                                                .toUpperCase();
                                            return (
                                                <Avatar key={emp.id} sx={{ bgcolor: theme.palette.primary.main }}>
                                                    {initials}
                                                </Avatar>
                                            );
                                        })}
                                    </AvatarGroup>

                                    {employeesWithCert.length > 4 && (
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                opacity: 0.6,
                                                display: 'block',
                                                mt: 1,
                                            }}
                                        >
                                            +{employeesWithCert.length - 4} weitere Mitarbeiter
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
        );
    }

    // List view
    return (
        <Box>
            {certificates.map((certificate, index) => (
                <Box
                    key={certificate.id}
                    onClick={() => navigate(`/${ELinks.CERTIFICATES}/${certificate.id}`)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        py: 1.5,
                        px: 1,
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                        },
                        ...(index < certificates.length - 1 && {
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        }),
                    }}
                >
                    <Box
                        component="img"
                        src="/certificates/certificate-placeholder.svg"
                        alt="Certificate"
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                        }}
                    />

                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 500,
                                fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.9rem' },
                            }}
                        >
                            {certificate.title}
                        </Typography>
                    </Box>

                    <Chip
                        label={getStatusText(certificate)}
                        size="small"
                        sx={{
                            bgcolor: alpha(getStatusColor(certificate), 0.1),
                            color: getStatusColor(certificate),
                            fontWeight: 500,
                            fontSize: '0.7rem',
                        }}
                    />
                </Box>
            ))}
        </Box>
    );
}

export default CertificatesList;
